import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import PersonalAiContextPanel from "../components/PersonalAiContextPanel";
import SecureFieldPanel from "../components/SecureFieldPanel";
import PhoneVerifyPanel from "../components/PhoneVerifyPanel";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { api, formatError, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useMenuVisibility } from "../context/MenuVisibilityContext";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail, User, ShieldCheck, ExternalLink, Copy, Gift, Trash2, UsersRound, Send,
  Camera, Lock, Phone, Save, Bot, CreditCard, LogOut,
} from "lucide-react";

// Iter 38 — horizontal tab menu matching /modules/:slug layout.
//
// Tabs (order + visibility come from Admin → Menu Directory → Account Page):
//   Profile              → picture, name, email, password, phone, sign out
//   Subscription         → plan card + team seats card
//   Referral Rewards     → referral link + credits
//   Personal AI Context  → AI context panel
//
// A tab disappears when its Menu Directory item is disabled. If every tab
// happens to be disabled, we fall back to rendering the Profile tab so
// the member is never locked out of their own account controls.
const TAB_DEFS = [
  { key: "profile",       menuId: "account-page-profile",       label: "Profile",             Icon: User },
  { key: "subscription",  menuId: "account-page-subscription",  label: "Subscription",        Icon: CreditCard },
  { key: "referrals",     menuId: "account-page-referrals",     label: "Referral Rewards",    Icon: Gift },
  { key: "personal-ai",   menuId: "account-page-personal-ai-context", label: "Personal AI Context", Icon: Bot },
];

export default function Account() {
  const { user, logout, setUser, refresh } = useAuth();
  const [ref, setRef] = useState(null);

  useEffect(() => { api.get("/referrals/me").then((r) => setRef(r.data)).catch(() => {}); }, []);
  const copy = () => {
    if (!ref?.share_url) return;
    navigator.clipboard.writeText(ref.share_url);
    toast.success("Link copied");
  };

  return (
    <AppShell title="Account & Access">
      <AccountBody
        user={user}
        logout={logout}
        setUser={setUser}
        refresh={refresh}
        ref_={ref}
        copy={copy}
      />
    </AppShell>
  );
}

// Child of <AppShell/> so `useMenuVisibility()` sees the real provider
// value (the provider is mounted inside AppShell). Calling the hook in
// the top-level page component returned the default context where
// `isDisabled` is a no-op, which caused disabled tabs to keep rendering.
function AccountBody({ user, logout, setUser, refresh, ref_, copy }) {
  const { isDisabled } = useMenuVisibility();
  const location = useLocation();
  const { sms_enabled: smsEnabled } = useFeatureFlags();

  const tabs = useMemo(() => {
    const enabled = TAB_DEFS.filter((t) => !(isDisabled && isDisabled(t.menuId)));
    return enabled.length ? enabled : [TAB_DEFS[0]];
  }, [isDisabled]);

  // Deep-link support so entries like "Refer a friend" (Profile Menu) or
  // "Team Seats" can jump straight to the right tab. Accepts either a
  // `?tab=<key>` query param or a legacy hash (`#referrals`, `#team`).
  // Hash mappings keep the pre-tab-refactor URLs working.
  const HASH_TO_TAB = {
    referrals: "referrals",
    team: "subscription",
    "data-export": "profile",
    "danger-zone": "profile",
  };
  const desiredTab = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const explicit = q.get("tab");
    if (explicit && tabs.some((t) => t.key === explicit)) return explicit;
    const hashKey = (location.hash || "").replace(/^#/, "");
    const mapped = HASH_TO_TAB[hashKey];
    if (mapped && tabs.some((t) => t.key === mapped)) return mapped;
    return null;
  }, [location.search, location.hash, tabs]);

  const [tab, setTab] = useState(desiredTab || tabs[0].key);
  useEffect(() => {
    // If the admin disables the current tab while the page is open, or
    // the initial tab list changes (menu visibility loaded after mount),
    // snap back to the first available tab. When the URL requests a
    // specific tab, honour it too so a deep link always wins.
    if (desiredTab && desiredTab !== tab) { setTab(desiredTab); return; }
    if (!tabs.some((t) => t.key === tab)) setTab(tabs[0].key);
  }, [tabs, tab, desiredTab]);

  return (
    <div className="w-full" data-testid="account-page">
      <PageHero
        eyebrow="Access & Identity"
        icon={ShieldCheck}
        title={<>Your <span className="text-grad">Account</span></>}
        subtitle="Edit your details below. Email and password changes require a text code sent to your verified phone."
      />

      {/* Sticky horizontal tab bar — mirrors /modules/:slug */}
        <div
          className="mt-8 border-b border-shell-border sticky top-[64px] md:top-[68px] z-20 bg-white/95 backdrop-blur -mx-3 md:-mx-6 lg:-mx-10 px-3 md:px-6 lg:px-10"
          data-testid="account-tabs"
        >
          <div className="flex gap-1 overflow-x-auto -mx-1 px-1">
            {tabs.map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={`account-tab-${key}`}
                className={[
                  "relative inline-flex items-center gap-2 px-4 py-3 text-sm transition-colors whitespace-nowrap",
                  tab === key ? "text-sky-500 font-semibold" : "text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                <Icon className="w-4 h-4" /> {label}
                {tab === key && (
                  <span className="absolute left-2 right-2 -bottom-px h-0.5 bg-sky-500 shadow-[0_0_8px_rgba(14,165,233,0.6)]" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-6" data-testid={`account-panel-${tab}`}>
          {tab === "profile" && (
            <>
              <ProfilePicturePanel user={user} setUser={setUser} />
              <NamePanel user={user} onSaved={refresh} />
              <SecureFieldPanel
                testid="account-email-panel"
                field="email"
                label="Email address"
                icon={Mail}
                purpose="email_change"
                currentValue={user?.email}
                disabled={!user?.phone_verified}
                disabledReason="Verify your phone number below to change your email."
                onSaved={refresh}
              />
              <SecureFieldPanel
                testid="account-password-panel"
                field="password"
                label="Password"
                icon={Lock}
                purpose="password_change"
                currentValue="•••••••••"
                disabled={smsEnabled && !user?.phone_verified}
                disabledReason="Verify your phone number below to change your password."
                onSaved={refresh}
              />
              {smsEnabled ? (
                <PhoneVerifyPanel user={user} onSaved={refresh} />
              ) : (
                <section
                  className="rounded-2xl border border-shell-border bg-muted/30 p-5"
                  data-testid="phone-verify-disabled"
                >
                  <p className="text-sm font-semibold text-foreground">Phone verification</p>
                  <p className="text-[13px] text-muted-foreground mt-1">
                    Phone verification is temporarily unavailable while we complete a
                    security review. Your account is fully secured by email — nothing
                    to do here. We'll email you when phone-based 2FA is back on.
                  </p>
                </section>
              )}
              <button
                onClick={logout}
                data-testid="account-logout"
                className="self-start inline-flex items-center gap-2 rounded-md border border-shell-border bg-white px-4 py-2 text-sm font-semibold hover:bg-muted"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </>
          )}

          {tab === "subscription" && (
            <>
              <div className="surface p-6" data-testid="account-plan">
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Subscription</p>
                <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-display text-2xl">
                      {(user?.package_slug || "free").replace(/^\w/, (c) => c.toUpperCase())}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {user?.package_status || "Active"}
                    </p>
                  </div>
                  <Link to="/account/subscription" className="btn-amber" data-testid="account-manage-plan">
                    Manage plan <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              <TeamSeats />
            </>
          )}

          {tab === "referrals" && (
            <div className="surface p-6" data-testid="account-referral">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
                <Gift className="w-3.5 h-3.5" /> Referral rewards
              </p>
              <h2 className="font-display text-2xl mt-2">Invite a founder, earn $50</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Every paid subscription from your link credits your account with $50 toward any plan.
              </p>
              {ref_ && (
                <>
                  <div className="mt-4 grid sm:grid-cols-3 gap-3">
                    <Card label="Your code" value={ref_.code} />
                    <Card label="Founders invited" value={ref_.invited} />
                    <Card label="Credits earned" value={`$${ref_.credits_total}`} />
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <input value={ref_.share_url} readOnly data-testid="referral-link"
                      className="flex-1 rounded-md border border-shell-border bg-white px-3 py-2 text-sm font-mono" />
                    <button onClick={copy} data-testid="referral-copy" className="btn-amber">
                      <Copy className="w-4 h-4" /> Copy
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "personal-ai" && <PersonalAiContextPanel />}
        </div>
      </div>
  );
}

function Card({ label, value }) {
  return (
    <div className="border border-shell-border rounded-md p-4">
      <p className="font-display text-2xl">{value}</p>
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}

function NamePanel({ user, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(user?.name || ""); }, [user?.name]);
  const changed = name.trim() && name.trim() !== (user?.name || "").trim();

  const save = async () => {
    if (!changed) return;
    setBusy(true);
    try {
      await api.patch("/account/name", { name: name.trim() });
      toast.success("Name updated");
      onSaved?.();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface p-6" data-testid="account-name-panel">
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
        <User className="w-3.5 h-3.5" /> Name
      </p>
      <div className="mt-3 flex flex-wrap gap-2 items-start">
        <input
          data-testid="account-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          className="flex-1 min-w-[240px] rounded-md border border-shell-border bg-white px-3 py-2 text-sm"
        />
        <button
          data-testid="account-name-save"
          onClick={save}
          disabled={!changed || busy}
          className="btn-primary text-sm disabled:opacity-60">
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function ProfilePicturePanel({ user, setUser }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const initials = (user?.name || user?.email || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const picture = user?.picture;

  const pick = () => fileRef.current?.click();
  const onChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image (PNG, JPG, WEBP, or GIF)."); return; }
    if (f.size > 3 * 1024 * 1024) { toast.error("Image is too large (3 MB max)."); return; }
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/profile/avatar`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Upload failed");
      }
      const fresh = await res.json();
      setUser(fresh);
      toast.success("Profile picture updated");
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove your profile picture?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/auth/profile/avatar");
      setUser(data);
      toast.success("Profile picture removed");
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface p-6" data-testid="account-avatar-panel">
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
        <Camera className="w-3.5 h-3.5" /> Profile picture
      </p>
      <div className="mt-4 flex items-center gap-5 flex-wrap">
        <div className="w-20 h-20 rounded-full bg-shell-side text-white grid place-items-center font-bold text-2xl overflow-hidden shrink-0" data-testid="account-avatar">
          {picture ? (
            <img src={picture} alt="Your profile" className="w-full h-full object-cover" data-testid="account-avatar-img" />
          ) : initials}
        </div>
        <div className="flex-1 min-w-[220px]">
          <p className="font-display text-xl">How you show up</p>
          <p className="text-sm text-muted-foreground mt-1">
            PNG, JPG, WEBP, or GIF · up to 3 MB. We resize it automatically.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={onChange} className="hidden" data-testid="account-avatar-input" />
            <button onClick={pick} disabled={busy} data-testid="account-avatar-upload"
              className="btn-primary text-sm disabled:opacity-60">
              <Camera className="w-4 h-4" /> {busy ? "Uploading…" : picture ? "Change photo" : "Upload photo"}
            </button>
            {picture && (
              <button onClick={remove} disabled={busy} data-testid="account-avatar-remove"
                className="text-xs font-semibold text-muted-foreground hover:text-destructive border border-shell-border rounded-full px-4 py-2">
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TeamSeats() {
  const { isDisabled } = useMenuVisibility();
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/team/seats").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  // Admin can hide the entire Team Seats card from the Subscription tab via
  // Admin Console → Menu Directory → Account Page → Team Seats card.
  if (isDisabled && isDisabled("account-page-team-seats")) return null;
  if (!data) return null;

  if (data.role === "teammate") {
    return (
      <div className="surface p-6" data-testid="account-team-member">
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
          <UsersRound className="w-3.5 h-3.5" /> Team seat
        </p>
        <h2 className="font-display text-2xl mt-2">Sharing {data.owner?.name || data.owner?.email}'s plan</h2>
        <p className="text-sm text-muted-foreground mt-1">
          You have full access to their <span className="font-semibold capitalize">{data.owner?.package_slug || "—"}</span> tier.
          Billing and seat management stay with the account owner.
        </p>
      </div>
    );
  }

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    if (!window.confirm(`Add ${inviteEmail}? A prorated $${(data.seat_price_cents/100).toFixed(0)}/month charge will be added to your subscription for this seat.`)) return;
    setBusy(true);
    try {
      await api.post("/team/seats", { email: inviteEmail });
      setInviteEmail("");
      await load();
      toast.success("Invite sent & billing updated");
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const revoke = async (id, email) => {
    if (!window.confirm(`Revoke ${email || "this teammate"}'s seat? They'll lose access immediately and your subscription will be credited for the unused time.`)) return;
    try { await api.delete(`/team/seats/${id}`); await load(); toast.success("Seat revoked & billing updated"); }
    catch (err) { toast.error(formatError(err)); }
  };

  const pricePerSeat = ((data.seat_price_cents || 0) / 100).toFixed(0);
  const monthlyTotal = (data.used * (data.seat_price_cents || 0) / 100).toFixed(0);

  return (
    <div className="surface p-6" data-testid="account-team-owner">
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
        <UsersRound className="w-3.5 h-3.5" /> Team seats
      </p>
      <div className="mt-2 flex items-center justify-between gap-4 flex-wrap">
        <h2 className="font-display text-2xl">Bring your co-founders</h2>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold" data-testid="team-seat-count">
          {data.used} active · ${monthlyTotal}/mo
        </p>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Each additional seat is <span className="font-semibold">${pricePerSeat}/month</span>, prorated and added to your next invoice.
        Teammates share your plan access — same databases, modules, and tools.
      </p>

      {!data.tier_ok && (
        <div className="mt-4 rounded-lg border border-pink-200 bg-pink-50 px-4 py-3 text-sm text-pink-900" data-testid="team-tier-warn">
          You need an active subscription before you can add team seats.{" "}
          <Link to="/account/subscription" className="font-semibold text-primary underline underline-offset-2">Choose a plan →</Link>
        </div>
      )}

      <form onSubmit={invite} className="mt-4 flex flex-wrap gap-2" data-testid="team-invite-form">
        <input type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
          placeholder="teammate@yourbrand.com" disabled={!data.tier_ok}
          data-testid="team-invite-email"
          className="flex-1 min-w-[220px] rounded-md border border-shell-border bg-white px-3 py-2 text-sm disabled:bg-muted disabled:text-muted-foreground" />
        <button type="submit" disabled={busy || !data.tier_ok}
          data-testid="team-invite-submit"
          className="btn-primary disabled:opacity-60">
          <Send className="w-4 h-4" /> {busy ? "Adding…" : `Add seat · $${pricePerSeat}/mo`}
        </button>
      </form>

      <div className="mt-4 grid gap-2">
        {data.seats.length === 0 ? (
          <p className="text-xs text-muted-foreground">No teammates yet.</p>
        ) : data.seats.map((s) => (
          <div key={s.id} data-testid={`team-seat-${s.id}`}
            className="flex items-center justify-between gap-3 border border-shell-border rounded-md px-3 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{s.invite_email}</p>
              <p className="text-[11px] text-muted-foreground">
                {s.status === "active" ? `Joined ${(s.accepted_at || "").slice(0, 10)}` : "Invite sent"}
              </p>
            </div>
            <span className={[
              "text-[10px] uppercase tracking-widest rounded px-2 py-0.5 font-semibold",
              s.status === "active" ? "bg-green-100 text-green-700" : "bg-pink-100 text-pink-700"
            ].join(" ")}>{s.status}</span>
            <button onClick={() => revoke(s.id, s.invite_email)} data-testid={`team-seat-revoke-${s.id}`}
              className="text-muted-foreground hover:text-destructive" title="Revoke seat">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
